import axios from 'axios'
import logger from '../utils/logger'

export const api = axios.create({
  // Use empty baseURL to go through Vite dev proxy (configured in vite.config.ts)
  // This enables proper authentication by ensuring the Authorization header is preserved
  // when proxying requests from browser → Vite dev server → API service.
  // All API calls use paths starting with /api (e.g., '/api/admin/query'), which Vite
  // proxies to the API service URL (http://localhost:3003 in local dev).
  // This approach avoids CORS issues and ensures cookies/headers are handled correctly.
  baseURL: '',
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Validate photo variants in API responses (prevent stringification bugs)
api.interceptors.response.use(
  (response) => {
    // Validate variants arrays in collection/photo responses
    if (response.data) {
      validateVariantsInResponse(response.data, response.config.url || 'unknown');
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

/**
 * Recursively validate that photo variants are arrays, not stringified
 * Prevents silent data corruption bugs that break AdaptivePhoto
 */
function validateVariantsInResponse(data: any, url: string, path: string = 'root'): void {
  if (!data || typeof data !== 'object') return;

  // Check if this object has a variants field
  if ('variants' in data && data.variants !== null && data.variants !== undefined) {
    if (!Array.isArray(data.variants)) {
      logger.error('API', '❌ API Response Validation Failed!', {
        url,
        path,
        variantsType: typeof data.variants,
        variantsValue: data.variants,
        data
      });
      throw new Error(
        `API Response Error: variants is ${typeof data.variants}, expected array. ` +
        `URL: ${url}, Path: ${path}. ` +
        `This indicates the API returned corrupted data (likely JSON.stringify'd).`
      );
    }
  }

  // Recursively check nested objects and arrays
  if (Array.isArray(data)) {
    data.forEach((item, index) => {
      validateVariantsInResponse(item, url, `${path}[${index}]`);
    });
  } else {
    Object.entries(data).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        validateVariantsInResponse(value, url, `${path}.${key}`);
      }
    });
  }
}

/**
 * Fetch a single critique by ID
 *
 * Purpose: Determine which photo contains a specific critique.
 * Used when URL contains ?comment=<id> without ?photo=<id>.
 *
 * Feature: 040-critique-url-state
 *
 * @param collectionId - Global ID of the collection
 * @param critiqueId - Global ID of the critique
 * @returns Critique object with photoId field
 */
export async function fetchCritique(collectionId: string, critiqueId: string) {
  const response = await api.get(`/collections/${collectionId}/critiques/${critiqueId}`)
  return response.data
}
